import { PermissionEnum } from '@creditsights/api-ui-web';
import { sumBy } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COMPANY_ROUTES, ROUTES } from '../../Base.constants';
import { dataToolsDropdownOptions } from '../../components/Header/Navigation/NavigationDropdownOptions/dataToolsDropdownOptions';
import { dealsDropdownOptions } from '../../components/Header/Navigation/NavigationDropdownOptions/dealsDropdownOptions';
import { marketStatsDropdownOptions } from '../../components/Header/Navigation/NavigationDropdownOptions/marketStatsDropdownOptions';
import { marketsDropdownOptions } from '../../components/Header/Navigation/NavigationDropdownOptions/marketsDropdownOptions';
import { ItemType, NavigationItem } from '../../models/Navigation';
import useFetchConfig from '../config/useFetchConfig';
import useFetchUserPermissions from '../permissions/useFetchUserPermissions';
import useFetchSectorOptions from './useFetchSectorOptions';

const RESIZE_HANDLER_CONFIG = {
  gap: 24,
  moreDropdownWidth: 64,
  dropdownWidth: 180,
  // main content padding
  padding: 12,
};

interface UseGetNavigationItemsResult {
  visibleItems: NavigationItem[];
  hiddenItems: NavigationItem[];
}

type NavigationItemConfig = NavigationItem & {
  forbidden?: boolean;
  hiddenByDefault?: boolean;
};

const useGetAllNavigationItems = () => {
  const location = useLocation();
  const isRouteActive = (route) => location.pathname.startsWith(route);
  const isCompanyRoute = () => COMPANY_ROUTES.some(isRouteActive);
  const { data: sectorsOptions } = useFetchSectorOptions();
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: config } = useFetchConfig();

  const NAVIGATION_ITEMS_CONFIG: NavigationItemConfig[] = useMemo(() => {
    return [
      {
        type: ItemType.NAVLINK,
        text: 'Feeds',
        to: ROUTES.feed,
        dataTestId: 'feed-link',
        width: 53,
      },
      {
        type: ItemType.DROPDOWN,
        options: marketsDropdownOptions(userPermissions?.permissions),
        text: 'Markets',
        dataTestId: 'markets-link',
        width: 81,
        forbidden: !userPermissions?.hasAccessToMarkets(),
      },
      {
        type: ItemType.DROPDOWN,
        options: sectorsOptions,
        text: 'Sectors',
        dataTestId: 'sectors-link',
        width: 79,
        forbidden: !userPermissions?.hasAccessToSectors(),
      },
      {
        type: ItemType.NAVLINK,
        text: 'Companies',
        to: ROUTES.companyList,
        dataTestId: 'companies-link',
        isActive: isCompanyRoute(),
        width: 85,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Risk Dashboard',
        to: ROUTES.dashboards,
        dataTestId: 'dashboards-link',
        width: 111,
        forbidden: !userPermissions?.hasAccessToRP(),
      },
      {
        type: ItemType.NAVLINK,
        text: 'Special Situations',
        to: ROUTES.specialSituations,
        dataTestId: 'special-situations-link',
        width: 124,
        forbidden: !userPermissions?.hasAccessToSpecialSituations(),
      },
      {
        type: ItemType.DROPDOWN,
        options: dataToolsDropdownOptions(userPermissions?.permissions),
        text: 'Data Tools',
        dataTestId: 'data-tools-link',
        isActive: isRouteActive(ROUTES.dataTools),
        width: 95,
        forbidden: !userPermissions?.hasAccessToDataTools(),
      },
      {
        type: ItemType.DROPDOWN,
        options: dealsDropdownOptions,
        text: 'Deals Pipeline',
        dataTestId: 'deals-link',
        isActive: isRouteActive(ROUTES.deals),
        width: 117,
        forbidden: !userPermissions?.hasAccessToDealsPipeline(),
      },
      {
        type: ItemType.DROPDOWN,
        options: marketStatsDropdownOptions,
        text: 'Market Stats',
        dataTestId: 'market-stats-link',
        isActive: isRouteActive(ROUTES.marketStats),
        width: 109,
        forbidden: !userPermissions?.hasAccessToMarketStats(),
      },
      {
        type: ItemType.NAVLINK,
        text: 'Our Team',
        dataTestId: 'our-team-link',
        to: '/analysts',
        width: 75,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Risk Products',
        dataTestId: 'risk-products',
        to: config?.riskProductsUrl!,
        isExternal: true,
        width: 115,
        forbidden: !userPermissions?.includes(PermissionEnum.RISK_PRODUCTS_EXTERNAL_LINK),
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Events',
        dataTestId: 'events-link',
        to: 'https://know.creditsights.com/events/',
        isExternal: true,
        width: 57,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'News',
        dataTestId: 'news-link',
        to: 'https://know.creditsights.com/news-features/',
        isExternal: true,
        width: 51,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Podcasts',
        dataTestId: 'podcasts-link',
        to: 'https://know.creditsights.com/resource-library/#podcasts',
        isExternal: true,
        width: 73,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Resources',
        dataTestId: 'resources-link',
        to: 'https://know.creditsights.com/resource-library/',
        isExternal: true,
        width: 80,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'Webinars',
        dataTestId: 'webinars-link',
        to: 'https://know.creditsights.com/resource-library/#webinars',
        isExternal: true,
        width: 74,
        hiddenByDefault: true,
      },
      {
        type: ItemType.NAVLINK,
        text: 'About Risk Products',
        dataTestId: 'rp-about-link',
        to: `${window.location.origin}/page/rp_about`,
        isExternal: true,
        width: 74,
        forbidden: !userPermissions?.includes(PermissionEnum.RISK_PRODUCTS),
        hiddenByDefault: true,
      },
    ];
  }, [sectorsOptions, location, userPermissions, config]);

  const data = useMemo(() => {
    if (!userPermissions) {
      return [];
    }

    return NAVIGATION_ITEMS_CONFIG.filter((item) => !item.forbidden);
  }, [userPermissions, NAVIGATION_ITEMS_CONFIG]);

  return { data };
};

const resizeHandler = (items: NavigationItemConfig[], setVisibleItems, setHiddenItems) => {
  const { gap, moreDropdownWidth, dropdownWidth, padding } = RESIZE_HANDLER_CONFIG;
  const visible: NavigationItemConfig[] = [];
  const hidden: NavigationItemConfig[] = [];
  // navigation width is constrained by the width of the opened more dropdown + main content padding on both sides (multiplied by 2).
  const maxNavWidth = window.innerWidth - 2 * (dropdownWidth - moreDropdownWidth + padding);

  // partition navigation items so that 1st level items does not exceed the maxNavWidth
  // items that don't "fit" as well as items marked as "hidden" are displayed under "More" dropdown
  for (const item of items) {
    if (!item.hiddenByDefault) {
      const currentNavWidth = sumBy(visible, (i) => i.width! + gap) + moreDropdownWidth; // add gap for each item because even last item should have it (gap between last item and 'More' dropdown)
      const newNavWidth = currentNavWidth + item.width! + gap;
      if (newNavWidth <= maxNavWidth) visible.push(item);
      else hidden.push(item);
    } else {
      hidden.push(item);
    }
  }

  setVisibleItems(visible);
  setHiddenItems(hidden);
};

export default (): UseGetNavigationItemsResult => {
  const [visibleItems, setVisibleItems] = useState<NavigationItem[]>([]);
  const [hiddenItems, setHiddenItems] = useState<NavigationItem[]>([]);
  const { data: items } = useGetAllNavigationItems();
  const itemsRef = useRef<NavigationItem[]>(items);

  useEffect(() => {
    itemsRef.current = items;
    resizeHandler(items, setVisibleItems, setHiddenItems);
  }, [items]);

  useEffect(() => {
    const onResize = () => resizeHandler(itemsRef.current, setVisibleItems, setHiddenItems);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return { visibleItems, hiddenItems };
};
